import { useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import StripeEmbeddedCheckout from './components/StripeEmbeddedCheckout';
import SEO from '../../components/feature/SEO';
import { PLANS_BY_PAYMENT_ID, getYearlyUsd } from '@/config/plans';

// PayPal configuration
const PAYPAL_CLIENT_ID = import.meta.env.VITE_PUBLIC_PAYPAL_CLIENT_ID ?? '';
const PAYPAL_CURRENCY = 'USD';

// ─────────────────────────────────────────────────────────────────────────────
// PAYPAL SUBSCRIPTION PLAN IDs — pre-created in PayPal Dashboard
// Dashboard: Pay & Get Paid → Subscriptions → Subscription Plans (P-xxx)
// ─────────────────────────────────────────────────────────────────────────────
const PAYPAL_PLAN_IDS_M: Record<string, string> = {
  '2': import.meta.env.VITE_PAYPAL_PLAN_STARTER_M    ?? '',
  '3': import.meta.env.VITE_PAYPAL_PLAN_CORE_M        ?? '',
  '4': import.meta.env.VITE_PAYPAL_PLAN_BUSINESS_M    ?? '',
  '5': import.meta.env.VITE_PAYPAL_PLAN_PROADVISOR_M  ?? '',
  '9': import.meta.env.VITE_PAYPAL_PLAN_TEST_M        ?? '',
};

const PAYPAL_PLAN_IDS_Y: Record<string, string> = {
  '2': import.meta.env.VITE_PAYPAL_PLAN_STARTER_Y    ?? '',
  '3': import.meta.env.VITE_PAYPAL_PLAN_CORE_Y        ?? '',
  '4': import.meta.env.VITE_PAYPAL_PLAN_BUSINESS_Y    ?? '',
  '5': import.meta.env.VITE_PAYPAL_PLAN_PROADVISOR_Y  ?? '',
  '9': import.meta.env.VITE_PAYPAL_PLAN_TEST_M        ?? '',
};

// ─────────────────────────────────────────────────────────────────────────────
// STRIPE PAYMENT LINKS — separate Monthly (M) and Yearly (Y) per plan
// In each link\'s After-payment settings, set the redirect to e.g.:
//   https://qboverifier.bio-mail.com/payment-complete?method=stripe&planId=2&planName=Starter&term=M
// ─────────────────────────────────────────────────────────────────────────────
const STRIPE_PAYMENT_LINKS_M: Record<string, string> = {
  '2': import.meta.env.VITE_STRIPE_LINK_STARTER_M    ?? '',
  '3': import.meta.env.VITE_STRIPE_LINK_CORE_M        ?? '',
  '4': import.meta.env.VITE_STRIPE_LINK_BUSINESS_M    ?? '',
  '5': import.meta.env.VITE_STRIPE_LINK_PROADVISOR_M  ?? '',
  '9': import.meta.env.VITE_STRIPE_LINK_TEST_M        ?? '',
};

const STRIPE_PAYMENT_LINKS_Y: Record<string, string> = {
  '2': import.meta.env.VITE_STRIPE_LINK_STARTER_Y    ?? '',
  '3': import.meta.env.VITE_STRIPE_LINK_CORE_Y        ?? '',
  '4': import.meta.env.VITE_STRIPE_LINK_BUSINESS_Y    ?? '',
  '5': import.meta.env.VITE_STRIPE_LINK_PROADVISOR_Y  ?? '',
  '9': import.meta.env.VITE_STRIPE_LINK_TEST_Y        ?? '',
};

// Registration plan ID mapping (payment page key → xactid plan number)
const REGISTRATION_PLAN_MAP: Record<string, string> = {
  'free': '1',
  '2': '2',
  '3': '3',
  '4': '4',
  '5': '5',
  '9': '9',
};

// Plan definitions — derived from the single source of truth in src/config/plans.ts
interface PlanDefinition {
  name: string;
  monthlyAmount: number;   // USD charged per month
  yearlyAmount: number;    // USD charged per year (15% discount applied)
  invoices: string;
}

const FREE_PLAN_DEFINITION: PlanDefinition = {
  name: 'Free',
  monthlyAmount: 0,
  yearlyAmount: 0,
  invoices: 'Up to 5 documents/month',
};

const PLAN_DEFINITIONS: Record<string, PlanDefinition> = {
  free: FREE_PLAN_DEFINITION,
  ...Object.fromEntries(
    Object.entries(PLANS_BY_PAYMENT_ID).map(([paymentId, plan]) => [
      paymentId,
      {
        name: plan.name,
        monthlyAmount: plan.monthlyUsd,
        yearlyAmount: getYearlyUsd(plan),
        invoices: plan.documents,
      } as PlanDefinition,
    ]),
  ),
};

// Helper: format a USD amount as "$X" or "$X,XXX"
function formatUSD(amount: number): string {
  return `$${amount.toLocaleString('en-US')}`;
}

// Helper: format a USD amount with 2 decimal places (for tax/totals)
function formatUSDDecimal(amount: number): string {
  return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function Payment() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ── Prefer router state (invisible) over URL params (visible) ──
  const stateData = location.state as { planId?: string; term?: string } | null;
  const planIdResolved: string = stateData?.planId ?? searchParams.get('plan') ?? '';
  const rawTermResolved = stateData?.term ?? searchParams.get('term') ?? '';
  const term: 'M' | 'Y' = rawTermResolved === 'Y' ? 'Y' : 'M';
  const isYearly = term === 'Y';

  const [planDef, setPlanDef] = useState<PlanDefinition | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<'paypal' | 'card' | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paypalLoaded, setPaypalLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const planFromState = location.state?.plan;

    // Legacy: if state carries an old PricingPlan object, map it to PLAN_DEFINITIONS by name
    if (planFromState && typeof planFromState === 'object') {
      const matchedKey = Object.keys(PLAN_DEFINITIONS).find(
        (k) => PLAN_DEFINITIONS[k].name === planFromState.name
      );
      if (matchedKey) {
        setPlanDef(PLAN_DEFINITIONS[matchedKey]);
        return;
      }
    }

    if (planIdResolved && PLAN_DEFINITIONS[planIdResolved]) {
      setPlanDef(PLAN_DEFINITIONS[planIdResolved]);
    } else {
      navigate('/pricing');
    }
  }, [location, navigate, searchParams]);

  // Current plan amount based on term
  const planAmount = planDef
    ? (isYearly ? planDef.yearlyAmount : planDef.monthlyAmount)
    : 0;
  const planAmountStr = formatUSD(planAmount);
  const billingLabel = isYearly ? 'Yearly' : 'Monthly';
  const periodSuffix = isYearly ? '/year' : '/month';

  // ── Tax calculation ──────────────────────────────────────────────────────
  const taxRate = parseFloat(import.meta.env.VITE_ADD_TAX ?? '0') || 0;
  const taxAmount = taxRate > 0 ? parseFloat((planAmount * taxRate / 100).toFixed(2)) : 0;
  const totalAmount = planAmount + taxAmount;
  const showTax = taxRate > 0 && taxAmount > 0;

  // Load PayPal SDK — subscription mode requires intent=subscription&vault=true
  useEffect(() => {
    if (paymentMethod === 'paypal' && !paypalLoaded) {
      const existingScript = document.querySelector(`script[src*="paypal.com/sdk/js"]`);
      if (existingScript) {
        document.body.removeChild(existingScript);
        if (window.paypal) {
          delete (window as any).paypal;
        }
      }

      const script = document.createElement('script');
      script.src = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&currency=${PAYPAL_CURRENCY}&intent=subscription&vault=true`;
      script.async = true;
      script.onload = () => {
        setPaypalLoaded(true);
        setError(null);
      };
      script.onerror = () => {
        setError('Failed to load PayPal. Please refresh the page and try again.');
      };
      document.body.appendChild(script);

      return () => {
        const sdkScript = document.querySelector(`script[src*="paypal.com/sdk/js"]`);
        if (sdkScript) {
          document.body.removeChild(sdkScript);
        }
      };
    }
  }, [paymentMethod, paypalLoaded]);

  // Initialize PayPal subscription buttons when SDK is loaded
  useEffect(() => {
    if (paypalLoaded && planDef && window.paypal) {
      const container = document.getElementById('paypal-button-container');
      if (container && container.children.length === 0) {

        // Resolve the correct Plan ID for this plan + term
        const planKey = planIdResolved;
        const planIdMap = isYearly ? PAYPAL_PLAN_IDS_Y : PAYPAL_PLAN_IDS_M;
        const paypalPlanId = planIdMap[planKey] ?? '';

        if (!paypalPlanId) {
          setError('PayPal subscription plan is not configured for this option. Please use Credit Card or contact support.');
          return;
        }

        try {
          window.paypal.Buttons({
            style: {
              layout: 'vertical',
              color: 'blue',
              shape: 'rect',
              label: 'subscribe',
              height: 45,
            },
            fundingSource: window.paypal.FUNDING.PAYPAL,
            createSubscription: (_data: any, actions: any) => {
              return actions.subscription.create({
                plan_id: paypalPlanId,
              });
            },
            onApprove: (data: any) => {
              setIsProcessing(true);
              const regPlanId = REGISTRATION_PLAN_MAP[planKey] || '1';

              // Pass via router state — keeps sensitive params out of the URL
              setTimeout(() => {
                navigate('/payment-complete', {
                  state: {
                    subscriptionID: data.subscriptionID || '',
                    planName: planDef.name,
                    planId: regPlanId,
                    term,
                    method: 'paypal',
                    amountTotal: totalAmount,
                    amountTax: taxAmount,
                  },
                });
              }, 1000);
            },
            onError: () => {
              setError('An error occurred with PayPal. Please try again or contact support.');
              setIsProcessing(false);
            },
            onCancel: () => {
              setIsProcessing(false);
            },
          }).render('#paypal-button-container').catch(() => {
            setError('Failed to initialize PayPal buttons. Please refresh the page.');
          });
        } catch {
          setError('Failed to initialize PayPal. Please refresh the page and try again.');
        }
      }
    }
  }, [paypalLoaded, planDef]);

  const handlePaymentMethodSelect = (method: 'paypal' | 'card') => {
    setPaymentMethod(method);
    setError(null);
  };

  if (!planDef) return null;

  // ── Payment method selection ──────────────────────────────────────────────
  if (!paymentMethod) {
    return (
      <div className="min-h-screen bg-white">
        <SEO title="Payment – QBOVerifier" description="Complete your QBOVerifier subscription payment." noIndex />
        <Header />
        <div className="py-20 px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                Choose Your Payment Method
              </h1>
              <p className="text-xl text-gray-600">
                Select how you&apos;d like to complete your purchase
              </p>
            </div>

            {/* Order summary */}
            <div className="bg-gray-50 rounded-xl p-6 mb-12 text-center">
              <p className="text-gray-600 mb-2">You selected</p>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">{planDef.name} Plan</h2>
              <div className="flex items-baseline justify-center gap-1 mb-1">
                <span className="text-4xl font-bold" style={{ color: '#29B3E6' }}>
                  {showTax ? formatUSDDecimal(totalAmount) : planAmountStr}
                </span>
                <span className="text-xl text-gray-600">{periodSuffix}</span>
              </div>
              {isYearly && (
                <p className="text-green-600 font-semibold text-sm mb-1">
                  15% annual discount applied
                </p>
              )}
              {showTax && (
                <p className="text-gray-500 text-sm mb-1">
                  Includes {taxRate}% tax ({formatUSDDecimal(taxAmount)})
                </p>
              )}
              <p className="text-gray-600">{planDef.invoices}</p>
              <span className="inline-block mt-3 px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                {billingLabel} billing
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <button
                onClick={() => handlePaymentMethodSelect('paypal')}
                className="bg-white border-2 border-gray-200 rounded-2xl p-12 hover:border-blue-400 hover:shadow-xl transition-all duration-300 group cursor-pointer"
              >
                <div className="flex flex-col items-center">
                  <div className="w-24 h-24 flex items-center justify-center mb-6 bg-blue-50 rounded-full group-hover:bg-blue-100 transition-colors">
                    <i className="ri-paypal-line text-5xl" style={{ color: '#0070BA' }}></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">PayPal</h3>
                  <p className="text-gray-600 text-center">
                    Pay securely with your PayPal account
                  </p>
                </div>
              </button>

              <button
                onClick={() => handlePaymentMethodSelect('card')}
                className="bg-white border-2 border-gray-200 rounded-2xl p-12 hover:border-blue-400 hover:shadow-xl transition-all duration-300 group cursor-pointer"
              >
                <div className="flex flex-col items-center">
                  <div className="w-24 h-24 flex items-center justify-center mb-6 bg-green-50 rounded-full group-hover:bg-green-100 transition-colors">
                    <i className="ri-bank-card-line text-5xl text-green-600"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Credit Card</h3>
                  <p className="text-gray-600 text-center">
                    Pay with Visa, Mastercard, or American Express
                  </p>
                </div>
              </button>
            </div>

            <div className="text-center mt-12">
              <button
                onClick={() => navigate('/pricing')}
                className="text-gray-600 hover:text-gray-900 font-semibold transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line mr-2"></i>
                Back to Pricing
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // ── PayPal payment view ───────────────────────────────────────────────────
  if (paymentMethod === 'paypal') {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="py-20 px-6">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6 bg-blue-50 rounded-full">
                <i className="ri-paypal-line text-5xl" style={{ color: '#0070BA' }}></i>
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">Complete Your Payment</h1>
              <p className="text-xl text-gray-600">Review your order and proceed with PayPal</p>
            </div>

            {error && (
              <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-8">
                <div className="flex items-start gap-3">
                  <i className="ri-error-warning-line text-red-600 text-xl mt-0.5"></i>
                  <p className="text-red-700">{error}</p>
                </div>
              </div>
            )}

            <div className="bg-white border-2 border-gray-200 rounded-2xl p-8 mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Order Summary</h2>
              <div className="space-y-0 mb-6">

                {/* Plan row */}
                <div className="flex justify-between items-start pb-4 border-b border-gray-200">
                  <div>
                    <p className="font-semibold text-gray-900">{planDef.name} Plan</p>
                    <p className="text-sm" style={{ color: '#29B3E6' }}>{planDef.invoices}</p>
                  </div>
                  <p className="text-xl font-bold text-gray-900">{planAmountStr}</p>
                </div>

                {/* Tax row — only shown when VITE_ADD_TAX > 0 */}
                {showTax && (
                  <div className="flex justify-between items-center py-4 border-b border-gray-200">
                    <p className="text-gray-700 font-medium">Tax ({taxRate}%)</p>
                    <p className="text-gray-900 font-semibold">{formatUSDDecimal(taxAmount)}</p>
                  </div>
                )}

                {/* Billing cycle row */}
                <div className="flex justify-between items-center py-4 border-b border-gray-200">
                  <p className="text-gray-500">Billing Cycle</p>
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-gray-700">{billingLabel}</p>
                    {isYearly && (
                      <span className="text-xs bg-green-100 text-green-700 font-semibold px-2 py-0.5 rounded-full">
                        Save 15%
                      </span>
                    )}
                  </div>
                </div>

                {/* Total row */}
                <div className="flex justify-between items-center pt-4 border-t-2 border-gray-300">
                  <p className="text-xl font-bold text-gray-900">Total Due Today</p>
                  <p className="text-3xl font-bold" style={{ color: '#29B3E6' }}>
                    {showTax ? formatUSDDecimal(totalAmount) : planAmountStr}
                  </p>
                </div>
              </div>

              <div className="bg-blue-50 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <i className="ri-information-line text-blue-600 text-xl mt-0.5"></i>
                  <p className="text-sm text-gray-700">
                    Click the PayPal button below to complete your payment securely.
                    After successful payment, you&apos;ll be automatically redirected to the registration page.
                  </p>
                </div>
              </div>

              {isProcessing ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                  <p className="text-gray-600">Processing your payment...</p>
                </div>
              ) : (
                <div id="paypal-button-container" className="min-h-[50px]"></div>
              )}
            </div>

            <div className="text-center">
              <button
                onClick={() => setPaymentMethod(null)}
                className="text-gray-600 hover:text-gray-900 font-semibold transition-colors whitespace-nowrap"
                disabled={isProcessing}
              >
                <i className="ri-arrow-left-line mr-2"></i>
                Choose Different Payment Method
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // ── Credit Card / Stripe embedded checkout ────────────────────────────────
  if (paymentMethod === 'card') {
    return (
      <StripeEmbeddedCheckout
        planId={planIdResolved}
        planName={planDef?.name || ''}
        term={term}
        planAmount={planAmount}
        invoices={planDef?.invoices || ''}
        billingLabel={billingLabel}
        isYearly={isYearly}
        taxRate={taxRate}
        taxAmount={taxAmount}
        totalAmount={totalAmount}
        onBack={() => setPaymentMethod(null)}
      />
    );
  }

  return null;
}
